import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const LayoutContext = createContext();

const MOBILE_BREAKPOINT = 768;

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within LayoutProvider');
  }
  return context;
};

export const LayoutProvider = ({ children }) => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);

  // Effective desktop collapsed state, seeded from the persisted preference.
  // May diverge from what's in localStorage after a transient collapseNav().
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    const saved = localStorage.getItem('navCollapsed');
    return saved === 'true';
  });

  // Mobile overlay open state.
  // On mobile the nav rests as icons-only. Opening the drawer slides it over content.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Active conversation ID for highlighting in nav
  const [activeConversationId, setActiveConversationId] = useState(null);

  // Track viewport changes
  useEffect(() => {
    const handleResize = () => {
      const nowMobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(prev => {
        if (prev === nowMobile) return prev;
        // Crossing the breakpoint in either direction: close mobile overlay
        setMobileNavOpen(false);
        return nowMobile;
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Note: the desktop preference is persisted in toggleNav, not in an effect on
  // desktopCollapsed. Only an explicit user toggle should rewrite the stored
  // preference — programmatic collapses (collapseNav) are transient, so a reload
  // restores whatever the user last chose.

  // Computed: the effective collapsed state that all consumers should use.
  //
  // Desktop: the effective state above (collapsed or expanded, pushes content)
  // Mobile:  "collapsed" when drawer is closed, "expanded" when drawer is open
  //
  // This lets all nav components (NewChatButtons, ConversationItem, AdminSection,
  // UserFooter) render the right content without any code changes to them.
  // The CSS .nav-collapsed class on app-layout also reflects this, so the CSS
  // collapsed-state rules in nav.css work correctly for both contexts.
  const navCollapsed = isMobile ? !mobileNavOpen : desktopCollapsed;

  // Single toggle — desktop pushes/shrinks, mobile opens/closes overlay.
  // This is the only path that writes the persisted desktop preference.
  const toggleNav = useCallback(() => {
    if (isMobile) {
      setMobileNavOpen(prev => !prev);
    } else {
      const next = !desktopCollapsed;
      localStorage.setItem('navCollapsed', next);
      setDesktopCollapsed(next);
    }
  }, [isMobile, desktopCollapsed]);

  const closeMobileNav = useCallback(() => {
    setMobileNavOpen(false);
  }, []);

  // Collapse regardless of context — desktop shrinks the rail, mobile closes the
  // drawer. Used when starting a conversation so chat gets the full width.
  // Transient: does not touch the persisted preference.
  const collapseNav = useCallback(() => {
    if (isMobile) {
      setMobileNavOpen(false);
    } else {
      setDesktopCollapsed(true);
    }
  }, [isMobile]);

  const value = {
    isMobile,
    navCollapsed,        // computed — the one source of truth for all consumers
    mobileNavOpen,       // needed by AppLayout for backdrop + CSS class
    toggleNav,           // single handler for all toggle buttons
    closeMobileNav,      // for backdrop click
    collapseNav,         // force-collapse (e.g. on conversation start)
    activeConversationId,
    setActiveConversationId,
    // Raw setters exposed for edge cases
    setDesktopCollapsed,
    setMobileNavOpen,
  };

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
};